import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { kategorierKey } from './kategorier';
import type { VedlikeholdEntry, VedlikeholdSection, VedlikeholdStatus } from '@/types/api';

export const vedlikeholdKey = (section: VedlikeholdSection) => ['vedlikehold', section] as const;

// Public list of live entries for a section. Generelt is grouped by kategori
// client-side; Dugnad (Iter 7b) renders flat.
export function useVedlikeholdEntries(section: VedlikeholdSection) {
  return useQuery({
    queryKey: vedlikeholdKey(section),
    queryFn: async () =>
      (await api.get<{ data: VedlikeholdEntry[] }>(`/vedlikehold?section=${section}`)).data,
  });
}

export type CreateVedlikeholdInput = {
  section: VedlikeholdSection;
  kategoriId: string | null;
  description: string;
  image: File | null;
};

// Multipart POST. We always go through FormData (vs JSON) so the add-image case
// works without branching the wire format. The Service is multipart-agnostic on
// the JSON-only side too.
function toCreateFormData(input: CreateVedlikeholdInput): FormData {
  const fd = new FormData();
  fd.append('section', input.section);
  if (input.kategoriId !== null) fd.append('kategori_id', input.kategoriId);
  fd.append('description', input.description);
  if (input.image !== null) fd.append('image', input.image);
  return fd;
}

export function useCreateVedlikeholdEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateVedlikeholdInput) =>
      api.postForm<{ data: VedlikeholdEntry }>('/vedlikehold', toCreateFormData(input)),
    onSuccess: (_, vars) => {
      void queryClient.invalidateQueries({ queryKey: vedlikeholdKey(vars.section) });
      // Kategori entriesCount changes when a new entry lands in it.
      void queryClient.invalidateQueries({ queryKey: kategorierKey });
    },
  });
}

export type UpdateVedlikeholdInput = {
  id: string;
  // section needed for cache invalidation, NOT sent to the server (section is
  // fixed at create — the server's UpdateVedlikeholdRequest doesn't accept it).
  section: VedlikeholdSection;
  kategoriId?: string | null;
  description?: string;
  image?: File | null;
  // true = detach the current image (mutually exclusive with `image`).
  removeImage?: boolean;
};

// Multipart PATCH via Laravel Form Method Spoofing — browsers can't submit a
// PATCH with multipart/form-data cleanly, so we POST + `_method=PATCH`.
function toUpdateFormData(input: UpdateVedlikeholdInput): FormData {
  const fd = new FormData();
  fd.append('_method', 'PATCH');
  if (input.kategoriId !== undefined && input.kategoriId !== null) {
    fd.append('kategori_id', input.kategoriId);
  }
  if (input.description !== undefined) fd.append('description', input.description);
  if (input.image instanceof File) fd.append('image', input.image);
  if (input.removeImage) fd.append('remove_image', '1');
  return fd;
}

export function useUpdateVedlikeholdEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateVedlikeholdInput) =>
      api.postForm<{ data: VedlikeholdEntry }>(`/vedlikehold/${input.id}`, toUpdateFormData(input)),
    onSuccess: (_, vars) => {
      void queryClient.invalidateQueries({ queryKey: vedlikeholdKey(vars.section) });
      // Reassigning kategori may shift entriesCount between two kategorier.
      if (vars.kategoriId !== undefined) {
        void queryClient.invalidateQueries({ queryKey: kategorierKey });
      }
    },
  });
}

export function useDeleteVedlikeholdEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; section: VedlikeholdSection }) =>
      api.delete<void>(`/vedlikehold/${id}`),
    onSuccess: (_, vars) => {
      void queryClient.invalidateQueries({ queryKey: vedlikeholdKey(vars.section) });
      void queryClient.invalidateQueries({ queryKey: kategorierKey });
    },
  });
}

// Dugnadsoppgaver — assign / reassign / clear. `assigneeUserId: null` clears the
// assignee (→ åpen); a user id assigns (→ tildelt). Open to any logged-in user, not
// own-scoped (ADR-0028). Always invalidates the dugnad list.
export function useAssignVedlikehold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, assigneeUserId }: { id: string; assigneeUserId: string | null }) =>
      api.put<{ data: VedlikeholdEntry }>(`/vedlikehold/${id}/assignee`, { assigneeUserId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vedlikeholdKey('dugnad') });
    },
  });
}

// Dugnadsoppgaver — set status (tildelt / utført / kansellert). `åpen` is reached by
// clearing the assignee (useAssignVedlikehold), never here, so the input type excludes
// it. Open to any logged-in user (ADR-0028).
export function useSetVedlikeholdStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: Exclude<VedlikeholdStatus, 'åpen'> }) =>
      api.patch<{ data: VedlikeholdEntry }>(`/vedlikehold/${id}/status`, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vedlikeholdKey('dugnad') });
    },
  });
}
